import {
  Box,
  Flex,
  InputWrapper,
  PrimaryButton,
  smBumps,
  TextInput,
  Typo,
} from '@wrisk/ui-components'
import React, {
  ChangeEvent,
  FunctionComponent,
  HTMLInputTypeAttribute,
  useCallback,
  useState,
} from 'react'
import { useAsyncCallback } from 'react-async-hook'

import { with404ErrorHandling } from '../../../../../../clients/api'
import { DataEntry } from '../../../../../../domain'
import { useApiErrorHandlingAsyncCallback } from '../../../../../../hooks/auth'
import {
  TKeyBuilder,
  useWriskTranslation,
} from '../../../../../../infrastructure/internationalisation'
import { usePrincipal } from '../../../../../authentication'

export interface DataEntrySearchViewProps {
  endpoint: string
  onSubmit: (dataEntry: DataEntry) => void
  tKey: TKeyBuilder
  validate: (text: string) => boolean
  placeholder: string
  characterLimit?: number
  inputType?: HTMLInputTypeAttribute
}

export const DataEntrySearchView: FunctionComponent<DataEntrySearchViewProps> = ({
  endpoint,
  onSubmit,
  tKey,
  validate,
  placeholder,
  characterLimit,
  inputType,
}) => {
  const { t } = useWriskTranslation()

  const { apiClient } = usePrincipal()

  const [inputText, setInputText] = useState('')
  const [searchTerm, setSearchTerm] = useState<string>()
  const [valid, setValid] = useState(false)

  const onInputChange = useCallback(({ target }: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(undefined)
    const value = target.value.toUpperCase()
    if (characterLimit && value.length <= characterLimit) {
      setInputText(value)
      setValid(validate(value))
    }
  }, [])

  const onFormSubmit = useApiErrorHandlingAsyncCallback(async () => {
    setSearchTerm(inputText)
    const dataEntry = await with404ErrorHandling(() =>
      apiClient.getDataEntry(endpoint, inputText),
    )
    if (dataEntry) {
      onSubmit(dataEntry)
    }
    return dataEntry
  })
  const onInputKeyPress = useAsyncCallback(async (event) => {
    if (event.key === 'Enter') {
      await onFormSubmit.execute()
    }
  })

  const errorText =
    searchTerm === inputText && onFormSubmit.result === null ? (
      <Box width={1} my={smBumps}>
        <Typo typoSize='sm'>{t(tKey('error-message'))}</Typo>
      </Box>
    ) : undefined

  return (
    <Flex width={1} flexDirection='column' alignItems='flex-start'>
      <InputWrapper>
        <TextInput
          width={1}
          type={inputType ?? 'text'}
          variant='ghost'
          value={inputText}
          onChange={onInputChange}
          placeholder={placeholder}
          onKeyPress={onInputKeyPress.execute}
        />
        <PrimaryButton
          minWidth='8rem'
          layout='small'
          loading={onFormSubmit.loading}
          disabled={!valid}
          onClick={onFormSubmit.execute}
          data-testid='search'
          whiteSpace='nowrap'
        >
          {t(tKey('check-code'))}
        </PrimaryButton>
      </InputWrapper>
      {errorText}
    </Flex>
  )
}
